import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// components
import {
  Stack,
  Button,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from '@mui/material';

// context
import { useUser } from 'shared/hooks/api/user';

// hooks
import {
  useIndustries,
  useResetDemoMutation,
  useUpdateDemoIndustryMutation,
} from 'shared/hooks/api/demo';

// styles
import * as Text from 'theme/components/Text.style';
import * as Container from 'layout/styles/PageLayout.style';
import colours from 'theme/atoms/colours';

const DemoSettings: React.FC = () => {
  const history = useHistory();
  const { data: user } = useUser();

  const { data: industries = [] } = useIndustries(user?.language || 'en');
  const { mutate: resetDemo, isPending: isResetting } = useResetDemoMutation();
  const { mutate: updateIndustry, isPending: isUpdating } =
    useUpdateDemoIndustryMutation();

  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect non-internal users away from this page
  useEffect(() => {
    if (!user?.is_internal) {
      history.push('/');
    }
  }, [user, history]);

  // Reset tour and demo sessions
  const resetTour = (): void => {
    window.localStorage.removeItem('hasCompletedTour');

    resetDemo(
      { user },
      {
        onSuccess: () => {
          setSuccessMessage(
            'Demo reset successful! Conversations have been reset.'
          );
          setShowSuccess(true);
        },
      }
    );
  };

  const handleIndustryChange = (event: SelectChangeEvent<string>): void => {
    setSelectedIndustry(event.target.value);
  };

  // Handle industry update
  const handleIndustryUpdate = (): void => {
    if (!selectedIndustry || !user?.id) return;

    updateIndustry(
      { industry: selectedIndustry, user_id: user.id },
      {
        onSuccess: () => {
          setSuccessMessage('Industry updated successfully!');
          setShowSuccess(true);
          window.location.reload();
        },
      }
    );
  };

  // Handle closing the success message
  const handleCloseSnackbar = (): void => {
    setShowSuccess(false);
  };

  // If not an internal user, don't render the page content
  if (!user?.is_internal) {
    return null;
  }

  return (
    <Container.PageContainer>
      <Stack
        spacing={4}
        sx={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}
      >
        <Text.H2>Demo Settings</Text.H2>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Text.H3>Reset Demo</Text.H3>

              <Button
                variant="contained"
                onClick={resetTour}
                disabled={isResetting}
                sx={{ backgroundColor: colours.primary[500] }}
              >
                {isResetting ? 'Resetting...' : 'Reset Demo'}
              </Button>

              <Typography variant="body2" color="textSecondary">
                This will reset the demo tour and conversations to the initial
                state.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Text.H3>Change Industry</Text.H3>

              <Typography variant="body2">
                Select an industry to update the demo content.
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="industry-select-label">Industry</InputLabel>
                <Select
                  labelId="industry-select-label"
                  id="industry-select"
                  value={selectedIndustry}
                  label="Industry"
                  onChange={handleIndustryChange}
                >
                  {industries.length === 0 ? (
                    <MenuItem value="">Loading...</MenuItem>
                  ) : (
                    industries.map((industry) => (
                      <MenuItem key={industry.id} value={industry.id}>
                        {industry.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleIndustryUpdate}
                  disabled={!selectedIndustry || isUpdating}
                  sx={{ backgroundColor: colours.primary[500] }}
                >
                  {isUpdating ? 'Updating...' : 'Confirm'}
                </Button>
              </Box>

              <Typography variant="body2" color="textSecondary">
                This will update the industry and update the demo content.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container.PageContainer>
  );
};

export default DemoSettings;
